/**
 * 欄位關係引擎服務
 * 處理表單欄位間的動態關係和互動邏輯
 */

import { Injectable } from '@angular/core';
import { FormGroup, AbstractControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { 
  FieldRelationship, 
  FieldAction, 
  FormRelationshipConfig, 
  RelationshipResult,
  DateValue 
} from './relationship-types';
import { getRelationshipConfig } from './relationship-configs';

@Injectable({
  providedIn: 'root'
})
export class FieldRelationshipService {
  private activeFormGroups = new Map<string, FormGroup>();
  private destroy$ = new Subject<void>();
  private debugMode = true; // 可透過環境變數控制

  /**
   * 註冊表單關係
   * @param formId 表單ID
   * @param formGroup FormGroup 實例
   */
  registerFormRelationships(formId: string, formGroup: FormGroup): void {
    console.log(`[FieldRelationship] 🚀 registerFormRelationships called with formId: ${formId}`);
    
    const config = getRelationshipConfig(formId);
    if (!config) {
      console.log(`[FieldRelationship] ❌ No relationship config found for formId: ${formId}`);
      return;
    }

    console.log(`[FieldRelationship] ✅ Found config for formId: ${formId}`, {
      relationshipCount: config.relationships.length,
      formName: config.formName,
      relationships: config.relationships.map(r => ({ id: r.id, when: r.when, thenCount: r.then.length }))
    });
    
    this.activeFormGroups.set(formId, formGroup);
    this.setupRelationshipListeners(formId, formGroup, config.relationships);
  }

  /**
   * 設置關係監聽器
   */
  private setupRelationshipListeners(formId: string, formGroup: FormGroup, relationships: FieldRelationship[]): void {
    console.log(`[FieldRelationship] 🔧 setupRelationshipListeners for ${relationships.length} relationships`);
    console.log(`[FieldRelationship] Available form controls:`, Object.keys(formGroup.controls));
    
    relationships.forEach((relationship, index) => {
      console.log(`[FieldRelationship] 📋 Setting up relationship ${index + 1}/${relationships.length}: ${relationship.id}`);
      const sourceControl = formGroup.get(relationship.when.field);
      
      if (sourceControl) {
        console.log(`[FieldRelationship] ✅ Found source control for ${relationship.when.field}`, {
          currentValue: sourceControl.value,
          condition: relationship.when
        });
        
        sourceControl.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(value => {
            console.log(`[FieldRelationship] 🔄 FormId ${formId} - Field ${relationship.when.field} changed to:`, value);
            this.evaluateRelationship(formId, formGroup, relationship, value);
          });
        
        // 初始執行一次 (處理表單載入時的初始值)
        setTimeout(() => {
          const initialValue = sourceControl.value;
          console.log(`[FieldRelationship] 🎯 Initial evaluation for ${relationship.when.field}:`, {
            relationshipId: relationship.id,
            value: initialValue,
            condition: relationship.when,
            willTrigger: this.willConditionTrigger(relationship.when, initialValue)
          });
          
          // 總是執行評估，讓 evaluateRelationship 決定是否觸發
          this.evaluateRelationship(formId, formGroup, relationship, initialValue);
        }, 200); // 增加延遲確保表單完全初始化
        
      } else {
        console.error(`[FieldRelationship] ❌ FormId ${formId} - Source field ${relationship.when.field} not found in form controls: ${Object.keys(formGroup.controls).join(', ')}`);
      }
    });
  }

  /**
   * 檢查條件是否會觸發（除錯用）
   */
  private willConditionTrigger(condition: any, value: any): boolean {
    if (condition.equals !== undefined) {
      return value === condition.equals;
    } else if (condition.notEquals !== undefined) {
      return value !== condition.notEquals;
    } else if (condition.in !== undefined) {
      return condition.in.includes(value);
    } else if (condition.notIn !== undefined) {
      return !condition.notIn.includes(value);
    } else if (condition.greaterThan !== undefined) {
      const numValue = parseFloat(value);
      return !isNaN(numValue) && numValue > condition.greaterThan;
    } else if (condition.lessThan !== undefined) {
      const numValue = parseFloat(value);
      return !isNaN(numValue) && numValue < condition.lessThan;
    } else if (condition.isEmpty !== undefined) {
      const isEmpty = !value || value === '' || value === null || value === undefined;
      return condition.isEmpty ? isEmpty : !isEmpty;
    } else if (condition.isNotEmpty !== undefined) {
      const isEmpty = !value || value === '' || value === null || value === undefined;
      return condition.isNotEmpty ? !isEmpty : isEmpty;
    }
    return false;
  }

  /**
   * 評估關係條件
   */
  private evaluateRelationship(
    formId: string, 
    formGroup: FormGroup, 
    relationship: FieldRelationship, 
    sourceValue: any
  ): RelationshipResult {
    const { when } = relationship;
    let conditionMet = false;

    // 檢查各種條件
    if (when.equals !== undefined) {
      conditionMet = sourceValue === when.equals;
    } else if (when.notEquals !== undefined) {
      conditionMet = sourceValue !== when.notEquals;
    } else if (when.in !== undefined) {
      conditionMet = when.in.includes(sourceValue);
    } else if (when.notIn !== undefined) {
      conditionMet = !when.notIn.includes(sourceValue);
    } else if (when.greaterThan !== undefined) {
      const numValue = parseFloat(sourceValue);
      conditionMet = !isNaN(numValue) && numValue > when.greaterThan;
    } else if (when.lessThan !== undefined) {
      const numValue = parseFloat(sourceValue);
      conditionMet = !isNaN(numValue) && numValue < when.lessThan;
    } else if (when.isEmpty !== undefined) {
      const isEmpty = !sourceValue || sourceValue === '' || sourceValue === null || sourceValue === undefined;
      conditionMet = when.isEmpty ? isEmpty : !isEmpty;
    } else if (when.isNotEmpty !== undefined) {
      const isEmpty = !sourceValue || sourceValue === '' || sourceValue === null || sourceValue === undefined;
      conditionMet = when.isNotEmpty ? !isEmpty : isEmpty;
    } else if (when.custom !== undefined) {
      try {
        conditionMet = when.custom(sourceValue);
      } catch (error) {
        console.error(`[FieldRelationship] Error in custom condition for relationship ${relationship.id}:`, error);
        conditionMet = false;
      }
    }

    if (this.debugMode) {
      console.log(`[FieldRelationship] Relationship ${relationship.id} condition met: ${conditionMet}`, {
        sourceValue,
        condition: when
      });
    }

    const result: RelationshipResult = {
      relationshipId: relationship.id,
      conditionMet,
      actionsExecuted: 0,
      errors: []
    };

    if (conditionMet) {
      relationship.then.forEach(action => {
        setTimeout(() => {
          try {
            this.executeAction(formGroup, action);
            result.actionsExecuted++;
          } catch (error) {
            const errorMsg = `Action ${action.action} on field ${action.field} failed: ${error}`;
            console.error(`[FieldRelationship] ${errorMsg}`);
            result.errors = result.errors || [];
            result.errors.push(errorMsg);
          }
        }, action.delay || 0);
      });
    }

    return result;
  }

  /**
   * 執行動作
   */
  private executeAction(formGroup: FormGroup, action: FieldAction): void {
    const targetControl = formGroup.get(action.field);
    if (!targetControl) {
      throw new Error(`Target field ${action.field} not found`);
    }

    if (this.debugMode) {
      console.log(`[FieldRelationship] Executing action ${action.action} on field ${action.field}`, {
        value: action.value,
        currentValue: targetControl.value
      });
    }

    switch (action.action) {
      case 'setValue':
        targetControl.setValue(action.value);
        break;

      case 'clearValue':
        targetControl.setValue('');
        break;

      case 'setRequired':
        this.setFieldRequired(targetControl, action.value);
        break;

      case 'setMinDate':
        this.setDateConstraint(targetControl, 'min', action.value);
        break;

      case 'setMaxDate':
        this.setDateConstraint(targetControl, 'max', action.value);
        break;

      case 'setStartDate':
        // 針對日期範圍元件設定開始日期
        this.setDateRangeValue(targetControl, 'startDate', action.value);
        break;

      case 'setEndDate':
        // 針對日期範圍元件設定結束日期
        this.setDateRangeValue(targetControl, 'endDate', action.value);
        break;

      case 'setMinStartDate':
        // 針對日期範圍元件設定最小開始日期
        console.log(`[FieldRelationship] Executing setMinStartDate action:`, {
          field: action.field,
          value: action.value,
          currentControlValue: targetControl.value
        });
        
        try {
          this.setDateRangeConstraint(targetControl, 'minStartDate', action.value);
          
          // 驗證約束是否成功設定
          const setConstraint = (targetControl as any)._minStartDate;
          console.log(`[FieldRelationship] setMinStartDate completed:`, {
            constraintSet: setConstraint,
            allConstraints: Object.keys(targetControl).filter(k => k.startsWith('_'))
          });
        } catch (error) {
          console.error(`[FieldRelationship] setMinStartDate failed:`, error);
        }
        break;

      case 'setMinEndDate':
        // 針對日期範圍元件設定最小結束日期
        this.setDateRangeConstraint(targetControl, 'minEndDate', action.value);
        break;

      case 'setVisible':
        this.setFieldProperty(action.field, 'visible', action.value);
        break;

      case 'setDisabled':
        if (action.value) {
          targetControl.disable();
        } else {
          targetControl.enable();
        }
        break;

      case 'setOptions':
        // 設定選項 (需要配合特定元件)
        this.setFieldProperty(action.field, 'options', action.value);
        break;

      case 'triggerReload':
        // 觸發元件重新載入
        this.triggerComponentReload(action.field);
        break;

      case 'refreshPersonnelList':
        // 重新整理人員清單 (針對 FormPersonnelSelectorComponent)
        this.refreshPersonnelSelector(action.field);
        break;

      case 'refreshData':
        // 重新整理資料 (針對動態 API 元件)
        this.refreshDynamicApiComponent(action.field);
        break;

      default:
        throw new Error(`Unknown action type: ${action.action}`);
    }
  }

  /**
   * 設定欄位必填狀態
   */
  private setFieldRequired(control: AbstractControl, required: boolean): void {
    if (required) {
      // 保留現有 validators 並新增 required
      const currentValidators = control.validator ? [control.validator] : [];
      control.setValidators([...currentValidators, Validators.required]);
    } else {
      // 移除 required validator (簡化版本)
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }

  /**
   * 設定日期限制
   */
  private setDateConstraint(control: AbstractControl, type: 'min' | 'max', value: DateValue): void {
    const dateValue = this.resolveDateValue(value, control);

    // 將日期限制保存到控制項的自定義屬性中
    (control as any)[`_${type}Date`] = dateValue;
    
    if (this.debugMode) {
      console.log(`[FieldRelationship] Set ${type} date constraint: ${dateValue}`);
    }
  }

  /**
   * 欄位屬性管理 (供 template 使用)
   */
  private fieldProperties = new Map<string, any>();

  private setFieldProperty(fieldName: string, property: string, value: any): void {
    if (!this.fieldProperties.has(fieldName)) {
      this.fieldProperties.set(fieldName, {});
    }
    this.fieldProperties.get(fieldName)![property] = value;
    
    if (this.debugMode) {
      console.log(`[FieldRelationship] Set field property ${fieldName}.${property} = ${value}`);
    }
  }

  /**
   * 取得欄位屬性
   */
  getFieldProperty(fieldName: string, property: string): any {
    return this.fieldProperties.get(fieldName)?.[property];
  }

  /**
   * 取得欄位的日期限制
   */
  getDateConstraint(fieldName: string, type: 'min' | 'max'): string | null {
    // 這個方法可以讓 template 取得日期限制
    const formGroups = Array.from(this.activeFormGroups.values());
    for (const formGroup of formGroups) {
      const control = formGroup.get(fieldName);
      if (control) {
        return (control as any)[`_${type}Date`] || null;
      }
    }
    return null;
  }

  /**
   * 檢查欄位是否可見
   */
  isFieldVisible(fieldName: string): boolean {
    const visible = this.getFieldProperty(fieldName, 'visible');
    return visible !== false; // 預設為可見
  }

  /**
   * 清理特定表單的關係
   */
  unregisterFormRelationships(formId: string): void {
    this.activeFormGroups.delete(formId);
    if (this.debugMode) {
      console.log(`[FieldRelationship] Unregistered relationships for formId: ${formId}`);
    }
  }

  /**
   * 清理所有資源
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.activeFormGroups.clear();
    this.fieldProperties.clear();
  }

  /**
   * 設定除錯模式
   */
  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /**
   * 觸發元件重新載入
   */
  private triggerComponentReload(fieldName: string): void {
    // 尋找對應的元件並觸發重新載入
    const element = this.findFieldElement(fieldName);
    if (element) {
      // 觸發 change 事件，讓元件重新載入資料
      const event = new Event('change', { bubbles: true });
      element.dispatchEvent(event);
      
      if (this.debugMode) {
        console.log(`[FieldRelationship] Triggered reload for field: ${fieldName}`);
      }
    } else {
      console.warn(`[FieldRelationship] Could not find element for field: ${fieldName}`);
    }
  }

  /**
   * 重新整理人員選擇器清單
   */
  private refreshPersonnelSelector(fieldName: string): void {
    // 尋找 FormPersonnelSelectorComponent 並呼叫其方法
    const element = this.findFieldElement(fieldName);
    if (element) {
      // 尋找包含該元件的 Angular 元素
      const angularElement = element.closest('app-form-personnel-selector');
      if (angularElement) {
        // 透過 DOM 事件通知元件刷新
        const customEvent = new CustomEvent('refreshPersonnelList', { 
          bubbles: true,
          detail: { fieldName }
        });
        angularElement.dispatchEvent(customEvent);
        
        if (this.debugMode) {
          console.log(`[FieldRelationship] Triggered personnel list refresh for field: ${fieldName}`);
        }
      } else {
        console.warn(`[FieldRelationship] Could not find FormPersonnelSelectorComponent for field: ${fieldName}`);
      }
    }
  }

  /**
   * 設定日期範圍元件的值
   */
  private setDateRangeValue(control: AbstractControl, dateType: 'startDate' | 'endDate', value: DateValue): void {
    const resolvedDate = this.resolveDateValue(value, control);
    
    // 取得目前的日期範圍值
    const currentValue = control.value || {};
    
    // 更新指定的日期
    const newValue = {
      ...currentValue,
      [dateType]: resolvedDate
    };
    
    control.setValue(newValue);
    
    if (this.debugMode) {
      console.log(`[FieldRelationship] Set ${dateType} to: ${resolvedDate}`, newValue);
    }
  }

  /**
   * 設定日期範圍元件的約束
   */
  private setDateRangeConstraint(control: AbstractControl, constraintType: string, value: DateValue): void {
    const resolvedDate = this.resolveDateValue(value, control);
    
    // 將約束保存到控制項的自定義屬性中
    (control as any)[`_${constraintType}`] = resolvedDate;
    
    console.log(`[FieldRelationship] Set ${constraintType} constraint:`, {
      constraintType,
      originalValue: value,
      resolvedDate,
      controlValue: control.value,
      allCustomProps: Object.keys(control).filter(key => key.startsWith('_'))
    });
    
    if (this.debugMode) {
      console.log(`[FieldRelationship] Set ${constraintType} constraint: ${resolvedDate}`);
    }
  }

  /**
   * 解析日期值
   */
  private resolveDateValue(value: DateValue, control?: AbstractControl): string {
    console.log(`[FieldRelationship] resolveDateValue called:`, { value, controlValue: control?.value });
    
    if (value === 'today') {
      const result = new Date().toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
      console.log(`[FieldRelationship] Resolved 'today' to:`, result);
      return result;
    }
    
    if (typeof value === 'string') {
      // 處理 field+Ndays 格式
      const fieldPlusDaysMatch = value.match(/^(.+)\+(\d+)days$/);
      if (fieldPlusDaysMatch) {
        const [, fieldName, daysStr] = fieldPlusDaysMatch;
        const days = parseInt(daysStr, 10);
        
        console.log(`[FieldRelationship] Processing field+days:`, { fieldName, days });
        
        // 從表單中取得指定欄位的值
        const sourceDate = this.getFieldDateValue(fieldName);
        console.log(`[FieldRelationship] Source date from ${fieldName}:`, sourceDate);
        
        if (sourceDate) {
          const targetDate = new Date(sourceDate);
          targetDate.setDate(targetDate.getDate() + days);
          const result = targetDate.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
          console.log(`[FieldRelationship] Calculated target date:`, result);
          return result;
        } else {
          console.log(`[FieldRelationship] No source date found for field: ${fieldName}`);
        }
      }
      
      // 處理 today+Ndays 格式
      const todayPlusDaysMatch = value.match(/^today\+(\d+)days$/);
      if (todayPlusDaysMatch) {
        const days = parseInt(todayPlusDaysMatch[1], 10);
        const targetDate = new Date();
        targetDate.setDate(targetDate.getDate() + days);
        
        // 根據目標控制項的類型決定日期格式
        const result = this.formatDateForConstraint(targetDate, control);
        console.log(`[FieldRelationship] today+${days}days resolved to:`, result);
        return result;
      }
      
      // 處理 startDate/endDate 引用
      if (value === 'startDate' && control) {
        const currentValue = control.value || {};
        return currentValue.startDate || '';
      }
      
      if (value === 'endDate' && control) {
        const currentValue = control.value || {};
        return currentValue.endDate || '';
      }
    }
    
    // 直接返回字串值
    return value.toString();
  }

  /**
   * 從表單中取得指定欄位的日期值
   */
  private getFieldDateValue(fieldName: string): string | null {
    console.log(`[FieldRelationship] getFieldDateValue called for: ${fieldName}`);
    
    const formGroups = Array.from(this.activeFormGroups.values());
    console.log(`[FieldRelationship] Found ${formGroups.length} active form groups`);
    
    for (const formGroup of formGroups) {
      console.log(`[FieldRelationship] Checking form group controls:`, Object.keys(formGroup.controls));
      
      const control = formGroup.get(fieldName);
      console.log(`[FieldRelationship] Control for ${fieldName}:`, {
        found: !!control,
        value: control?.value,
        valueType: typeof control?.value
      });
      
      if (control && control.value) {
        // 如果是 timestamp 類型，取得其值
        if (typeof control.value === 'object' && control.value.value) {
          const result = new Date(control.value.value).toISOString();
          console.log(`[FieldRelationship] Found timestamp object, converted to: ${result}`);
          return result;
        }
        // 如果是日期字串
        if (typeof control.value === 'string') {
          console.log(`[FieldRelationship] Found date string: ${control.value}`);
          return control.value;
        }
        // 如果是 Date 物件
        if (control.value instanceof Date) {
          const result = control.value.toISOString();
          console.log(`[FieldRelationship] Found Date object, converted to: ${result}`);
          return result;
        }
      }
    }
    
    console.log(`[FieldRelationship] No valid date found for field: ${fieldName}`);
    return null;
  }

  /**
   * 重新整理動態 API 元件
   */
  private refreshDynamicApiComponent(fieldName: string): void {
    const element = this.findFieldElement(fieldName);
    if (element) {
      // 尋找動態 API 元件
      const apiElement = element.closest('app-dynamic-api-table, app-dynamic-api-select');
      if (apiElement) {
        // 透過 DOM 事件通知元件刷新
        const customEvent = new CustomEvent('refreshData', { 
          bubbles: true,
          detail: { fieldName }
        });
        apiElement.dispatchEvent(customEvent);
        
        if (this.debugMode) {
          console.log(`[FieldRelationship] Triggered data refresh for field: ${fieldName}`);
        }
      }
    }
  }

  /**
   * 根據控制項類型格式化日期約束
   */
  private formatDateForConstraint(date: Date, control?: AbstractControl): string {
    // 檢查是否為日期範圍元件
    if (control && this.isDateRangeControl(control)) {
      // 檢查 dateType 屬性來決定格式
      const dateType = this.getDateTypeFromControl(control);
      
      if (dateType === 'datetime') {
        return date.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
      } else {
        return date.toISOString().slice(0, 10); // YYYY-MM-DD
      }
    }
    
    // 預設使用日期格式
    return date.toISOString().slice(0, 10); // YYYY-MM-DD
  }

  /**
   * 檢查是否為日期範圍控制項
   */
  private isDateRangeControl(control: AbstractControl): boolean {
    // 檢查控制項的值是否有 startDate 和 endDate 屬性
    const value = control.value;
    return value && typeof value === 'object' && 
           (value.hasOwnProperty('startDate') || value.hasOwnProperty('endDate'));
  }

  /**
   * 從控制項推斷日期類型
   */
  private getDateTypeFromControl(control: AbstractControl): string {
    // 這裡可以嘗試從 DOM 元素或其他方式取得 dateType
    // 暫時預設為 'date'，因為大部分情況下是純日期
    return 'date';
  }

  /**
   * 尋找表單欄位元素
   */
  private findFieldElement(fieldName: string): HTMLElement | null {
    const selectors = [
      `[name="${fieldName}"]`,
      `#${fieldName}`,
      `[data-field-name="${fieldName}"]`
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        return element;
      }
    }

    return null;
  }
}